import { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";

const LINKS = [
  ["Home", "home"],
  ["About", "about"],
  ["Joint Health", "joint-health"],
  ["Career", "career"],
  ["Blog", "blog"],
  ["Press", "press"],
  ["Contact", "contact"],
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md shadow-[0_8px_30px_-20px_rgba(75,31,99,0.35)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
          aria-label="Dr. Maggie Cadet, MD, go to top"
        >
          <img
            src="/images/logo-stack.webp"
            alt="Dr. Maggie Cadet, MD logo"
            width="112"
            height="48"
            fetchPriority="high"
            className="h-12 w-auto object-contain transition-transform duration-700 group-hover:scale-105 group-hover:-rotate-1"
            loading="eager"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(([label, id], i) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => go(id)}
              style={{ animationDelay: `${i * 70}ms` }}
              className="nav-link relative text-[12px] tracking-[0.22em] uppercase text-charcoal hover:text-plum transition-colors"
            >
              {label}
              <span className="absolute left-0 -bottom-1.5 h-px w-0 bg-gradient-to-r from-plum via-royal to-violet transition-all duration-500 group-hover:w-full" />
            </button>
          ))}
          <a
            data-testid="nav-instagram"
            href="https://www.instagram.com/drmaggiecadet"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Dr. Maggie on Instagram"
            className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-lavender text-royal hover:bg-royal hover:text-white hover:rotate-[18deg] hover:scale-110 transition-all duration-500"
          >
            <Instagram size={16} strokeWidth={1.7} />
          </a>
        </nav>

        <button
          data-testid="nav-toggle"
          className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-lavender text-plum"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile" className="lg:hidden bg-white/95 backdrop-blur border-t border-lavender/40">
          <div className="px-6 py-4 flex flex-col gap-3">
            {LINKS.map(([label, id]) => (
              <button
                key={id}
                data-testid={`nav-mobile-${id}`}
                onClick={() => go(id)}
                className="text-left text-[13px] tracking-[0.22em] uppercase py-2 text-charcoal hover:text-plum"
              >
                {label}
              </button>
            ))}
            <a
              data-testid="nav-mobile-instagram"
              href="https://www.instagram.com/drmaggiecadet"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[13px] tracking-[0.22em] uppercase py-2 text-royal"
            >
              <Instagram size={14} /> Instagram
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
